import { useEffect, useState, type ReactNode } from "react";
import { Images } from "lucide-react";
import { useAuth } from "../../../shared/auth/auth.contexto";
import { cn } from "@base/ui";

const blobsEmCache = new Map<string, string>();

function ehUrlAssinada(src: string): boolean {
  return src.includes("sig=");
}

/**
 * Midia autenticada. URLs assinadas (sig=) o browser carrega direto.
 * Caminhos da API sem assinatura: fetch Bearer → blob (com cache de sessão).
 */
export function MidiaAutenticada({
  src,
  className,
  tipo = "image",
  controls = false,
  autoPlay = false,
  alt = "",
  poster,
  aoDimensoes,
  fallback,
}: {
  readonly src: string | null;
  readonly className?: string | undefined;
  readonly tipo?: "image" | "video";
  readonly controls?: boolean;
  readonly autoPlay?: boolean;
  readonly alt?: string;
  readonly poster?: string | null;
  readonly aoDimensoes?: (largura: number, altura: number) => void;
  readonly fallback?: ReactNode;
}) {
  const { tokenAtual } = useAuth();
  const [urlPronta, definirUrlPronta] = useState<string | null>(null);
  const [falhou, definirFalhou] = useState(false);

  useEffect(() => {
    if (!src) {
      definirUrlPronta(null);
      definirFalhou(false);
      return;
    }

    if (!src.startsWith("/api/") || ehUrlAssinada(src)) {
      definirUrlPronta(src);
      definirFalhou(false);
      return undefined;
    }

    const cached = blobsEmCache.get(src);
    if (cached) {
      definirUrlPronta(cached);
      definirFalhou(false);
      return undefined;
    }

    let cancelado = false;
    let objectUrl: string | null = null;
    const token = tokenAtual();

    void (async () => {
      try {
        const resposta = await fetch(src, {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
        const blob = await resposta.blob();
        objectUrl = URL.createObjectURL(blob);
        blobsEmCache.set(src, objectUrl);
        if (!cancelado) {
          definirUrlPronta(objectUrl);
          definirFalhou(false);
        }
      } catch {
        if (!cancelado) {
          definirUrlPronta(null);
          definirFalhou(true);
        }
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [src, tokenAtual]);

  if (falhou || !src) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div
        className={cn(
          "flex size-full items-center justify-center text-muted-foreground",
          className,
        )}
      >
        <Images className="size-8" aria-hidden="true" />
      </div>
    );
  }

  if (!urlPronta) {
    return (
      <div
        className={cn("size-full animate-pulse bg-muted", className)}
        aria-hidden="true"
      />
    );
  }

  if (tipo === "video") {
    return (
      <video
        src={urlPronta}
        poster={poster ?? undefined}
        className={className}
        controls={controls}
        autoPlay={autoPlay}
        playsInline
        preload={controls ? "auto" : "metadata"}
        onLoadedMetadata={(evento) => {
          const el = evento.currentTarget;
          if (el.videoWidth > 0 && el.videoHeight > 0) {
            aoDimensoes?.(el.videoWidth, el.videoHeight);
          }
        }}
      />
    );
  }

  return (
    <img
      src={urlPronta}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => definirFalhou(true)}
      onLoad={(evento) => {
        const el = evento.currentTarget;
        if (el.naturalWidth > 0 && el.naturalHeight > 0) {
          aoDimensoes?.(el.naturalWidth, el.naturalHeight);
        }
      }}
    />
  );
}
